/**
 * Admin → Event Approvals.
 *
 * Practitioners can now put events on the community Calendar, but nothing lands there unseen
 * (owner, Jun 2026). Approve it, send it back with a note, or decline. A change to an approved
 * event's date, price or cap waits here too, while the approved version stays live.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, MapPin, Link2, Users, Ticket, Clock, AlertCircle, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  notice: 'Notice only',
  rsvp: 'RSVP',
  ticketed: 'Ticketed',
};

const STATUS_STYLE = {
  pending: 'bg-amber-900/40 text-amber-100 border-amber-700',
  approved: 'bg-emerald-900/40 text-emerald-100 border-emerald-700',
  changes_requested: 'bg-sky-900/40 text-sky-100 border-sky-700',
  declined: 'bg-rose-900/40 text-rose-100 border-rose-700',
  cancelled: 'bg-neutral-800 text-neutral-200 border-neutral-600',
  draft: 'bg-neutral-800 text-neutral-200 border-neutral-600',
};

export default function AdminEventApprovalsTab() {
  return (
    <div className="space-y-8">
      <EventQueue />
      <PollQueue />
    </div>
  );
}

function EventQueue() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({ pending_count: 0, change_count: 0 });
  const [filter, setFilter] = useState('pending');
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events?status=${filter}`, { headers: headers() });
      setEvents(res.data?.events || []);
      setCounts({
        pending_count: res.data?.pending_count || 0,
        change_count: res.data?.change_count || 0,
      });
    } catch {
      setEvents([]);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const note = (notes[id] || '').trim();
    if (action !== 'approve' && action === 'request-changes' && !note) {
      alert('Say what needs changing — a bare rejection helps nobody.');
      return;
    }
    setBusy(id);
    try {
      await axios.post(`${API}/admin/events/${id}/${action}`, { note }, { headers: headers() });
      setNotes((n) => ({ ...n, [id]: '' }));
      await load();
      window.dispatchEvent(new Event('admin-data-changed'));
    } catch (e) {
      alert(e.response?.data?.detail || 'That did not go through.');
    }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="admin-event-approvals">
      <Card className="bg-neutral-900 border-neutral-700">
        <CardHeader>
          <CardTitle className="text-neutral-100 flex items-center gap-2">
            <CalendarDays size={18} /> Event Approvals
          </CardTitle>
          <p className="text-sm text-neutral-300">
            Events practitioners have submitted for the community Calendar.
            {counts.change_count > 0 && (
              <span className="text-amber-200">
                {' '}{counts.change_count} approved event
                {counts.change_count === 1 ? ' has' : 's have'} a change waiting.
              </span>
            )}
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              ['pending', `Awaiting review (${counts.pending_count})`],
              ['approved', 'On the calendar'],
              ['changes_requested', 'Sent back'],
              ['declined', 'Declined'],
              ['all', 'Everything'],
            ].map(([value, label]) => (
              <Button
                key={value}
                size="sm"
                variant={filter === value ? 'default' : 'outline'}
                className={filter === value
                  ? 'bg-amber-700 hover:bg-amber-600 text-white'
                  : 'border-neutral-600 text-neutral-200 hover:text-white hover:bg-neutral-800'}
                onClick={() => setFilter(value)}
                data-testid={`event-filter-${value}`}
              >
                {label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-neutral-300 text-sm" data-testid="event-approvals-loading">Loading…</p>
      ) : events.length === 0 ? (
        <Card className="bg-neutral-900 border-neutral-700">
          <CardContent className="py-10 text-center">
            <CalendarDays size={28} className="mx-auto mb-3 text-neutral-500" />
            <p className="text-neutral-200">Nothing here.</p>
            <p className="text-neutral-400 text-sm mt-1">
              Submitted events appear here for your review.
            </p>
          </CardContent>
        </Card>
      ) : (
        events.map((ev) => (
          <Card key={ev.id} className="bg-neutral-900 border-neutral-700"
            data-testid={`event-review-${ev.id}`}>
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-neutral-100 text-lg font-semibold">{ev.title}</h3>
                  <p className="text-neutral-400 text-sm">
                    {ev.host_name} · submitted{' '}
                    {ev.submitted_at ? new Date(ev.submitted_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`border ${STATUS_STYLE[ev.status] || STATUS_STYLE.draft}`}>
                    {(ev.status || 'draft').replace('_', ' ')}
                  </Badge>
                  <Badge className="border border-neutral-600 bg-neutral-800 text-neutral-100">
                    {KIND_LABEL[ev.kind] || ev.kind}
                  </Badge>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-2 text-sm text-neutral-200">
                <span className="flex items-center gap-2">
                  <Clock size={14} className="text-neutral-400" />
                  {ev.event_date} at {ev.event_time} ({ev.timezone})
                </span>
                <span className="flex items-center gap-2">
                  {ev.venue_mode === 'in_person'
                    ? <><MapPin size={14} className="text-neutral-400" /> {ev.venue_address}</>
                    : <><Link2 size={14} className="text-neutral-400" /> {ev.link}</>}
                </span>
                {ev.capacity ? (
                  <span className="flex items-center gap-2">
                    <Users size={14} className="text-neutral-400" /> Cap {ev.capacity}
                    {ev.waitlist_enabled ? ' · waiting list on' : ''}
                  </span>
                ) : null}
                {ev.kind === 'ticketed' && (
                  <span className="flex items-center gap-2">
                    <Ticket size={14} className="text-neutral-400" />
                    ${Number(ev.price || 0).toFixed(2)} · {ev.refund_policy_text}
                  </span>
                )}
              </div>

              {ev.description && (
                <p className="text-neutral-300 text-sm whitespace-pre-line border-l-2 border-neutral-700 pl-3">
                  {ev.description}
                </p>
              )}

              {ev.pending_changes && (
                <div className="rounded-md border border-amber-700 bg-amber-950/40 p-3"
                  data-testid={`event-pending-changes-${ev.id}`}>
                  <p className="text-amber-100 text-sm font-semibold flex items-center gap-2">
                    <AlertCircle size={14} /> Change waiting on a live event
                  </p>
                  <ul className="text-amber-100/90 text-sm mt-1 space-y-0.5">
                    {Object.entries(ev.pending_changes)
                      .filter(([k]) => k !== 'requested_at')
                      .map(([k, v]) => (
                        <li key={k}>{k.replace('_', ' ')}: <strong>{String(v)}</strong></li>
                      ))}
                  </ul>
                  <p className="text-amber-200/70 text-xs mt-2">
                    The approved version stays on the calendar until you approve this.
                  </p>
                </div>
              )}

              {ev.admin_note && (
                <p className="text-sm text-sky-100 bg-sky-950/40 border border-sky-800 rounded-md p-2">
                  Your note: {ev.admin_note}
                </p>
              )}

              <Textarea
                value={notes[ev.id] || ''}
                onChange={(e) => setNotes((n) => ({ ...n, [ev.id]: e.target.value }))}
                placeholder="A note to the practitioner (needed when sending it back)"
                className="bg-neutral-950 border-neutral-700 text-neutral-100 placeholder:text-neutral-500"
                rows={2}
                data-testid={`event-note-${ev.id}`}
              />

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-600 text-white"
                  disabled={busy === ev.id}
                  onClick={() => act(ev.id, 'approve')}
                  data-testid={`event-approve-${ev.id}`}
                >
                  {ev.pending_changes ? 'Approve the change' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-sky-700 text-sky-100 hover:bg-sky-900 hover:text-white"
                  disabled={busy === ev.id}
                  onClick={() => act(ev.id, 'request-changes')}
                  data-testid={`event-changes-${ev.id}`}
                >
                  Send back with a note
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-rose-700 text-rose-100 hover:bg-rose-900 hover:text-white"
                  disabled={busy === ev.id}
                  onClick={() => act(ev.id, 'decline')}
                  data-testid={`event-decline-${ev.id}`}
                >
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}


/**
 * The same queue, for polls. They are a different thing to a practitioner but the same job for
 * you: read it, approve it, or send it back with a note.
 */
function PollQueue() {
  const [polls, setPolls] = useState([]);
  const [pending, setPending] = useState(0);
  const [filter, setFilter] = useState('pending');
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/polls?status=${filter}`, { headers: headers() });
      setPolls(res.data?.polls || []);
      setPending(res.data?.pending_count || 0);
    } catch {
      setPolls([]);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    const note = (notes[id] || '').trim();
    if (action === 'request-changes' && !note) {
      alert('Say what needs changing.');
      return;
    }
    setBusy(id);
    try {
      await axios.post(`${API}/admin/polls/${id}/${action}`, { note }, { headers: headers() });
      setNotes((n) => ({ ...n, [id]: '' }));
      await load();
      window.dispatchEvent(new Event('admin-data-changed'));
    } catch (e) {
      alert(e.response?.data?.detail || 'That did not go through.');
    }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="admin-poll-approvals">
      <Card className="bg-neutral-900 border-neutral-700">
        <CardHeader>
          <CardTitle className="text-neutral-100 flex items-center gap-2">
            <BarChart3 size={18} /> Poll Approvals
          </CardTitle>
          <p className="text-sm text-neutral-300">
            Polls and surveys practitioners want to put to each other. {pending} awaiting review.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[['pending', 'Awaiting review'], ['open', 'Open'], ['closed', 'Closed'],
              ['changes_requested', 'Sent back'], ['declined', 'Declined'], ['all', 'Everything']]
              .map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  variant={filter === value ? 'default' : 'outline'}
                  className={filter === value
                    ? 'bg-amber-700 hover:bg-amber-600 text-white'
                    : 'border-neutral-600 text-neutral-200 hover:text-white hover:bg-neutral-800'}
                  onClick={() => setFilter(value)}
                  data-testid={`poll-filter-${value}`}
                >
                  {label}
                </Button>
              ))}
          </div>
        </CardContent>
      </Card>

      {polls.length === 0 ? (
        <Card className="bg-neutral-900 border-neutral-700">
          <CardContent className="py-8 text-center text-neutral-300">
            Nothing here.
          </CardContent>
        </Card>
      ) : polls.map((p) => (
        <Card key={p.id} className="bg-neutral-900 border-neutral-700"
          data-testid={`poll-review-${p.id}`}>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-neutral-100 text-lg font-semibold">{p.title}</h3>
                <p className="text-neutral-400 text-sm">
                  {p.author_name} · {p.questions?.length} question
                  {p.questions?.length === 1 ? '' : 's'} ·{' '}
                  {p.anonymous ? 'anonymous' : 'named'} ·{' '}
                  {p.closes_at ? `closes ${new Date(p.closes_at).toLocaleString()}`
                    : 'closed by hand'}
                </p>
              </div>
              <Badge className={`border ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`}>
                {(p.status || 'draft').replace('_', ' ')}
              </Badge>
            </div>

            {p.intro && <p className="text-neutral-300 text-sm">{p.intro}</p>}

            <ol className="text-sm text-neutral-200 space-y-1 list-decimal list-inside">
              {(p.questions || []).map((q) => (
                <li key={q.id}>
                  {q.prompt}
                  <span className="text-neutral-400">
                    {' '}— {q.type === 'text' ? 'written answer'
                      : q.type === 'multi' ? 'pick any'
                      : q.type === 'date' ? 'pick a date' : 'pick one'}
                    {q.options?.length ? `: ${q.options.join(', ')}` : ''}
                  </span>
                </li>
              ))}
            </ol>

            {p.admin_note && (
              <p className="text-sm text-sky-100 bg-sky-950/40 border border-sky-800 rounded-md p-2">
                Your note: {p.admin_note}
              </p>
            )}

            {p.status === 'pending' && (
              <>
                <Textarea
                  value={notes[p.id] || ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                  placeholder="A note to the practitioner (needed when sending it back)"
                  className="bg-neutral-950 border-neutral-700 text-neutral-100 placeholder:text-neutral-500"
                  rows={2}
                  data-testid={`poll-note-${p.id}`}
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="bg-emerald-700 hover:bg-emerald-600 text-white"
                    disabled={busy === p.id} onClick={() => act(p.id, 'approve')}
                    data-testid={`poll-approve-${p.id}`}>
                    Approve and open it
                  </Button>
                  <Button size="sm" variant="outline"
                    className="border-sky-700 text-sky-100 hover:bg-sky-900 hover:text-white"
                    disabled={busy === p.id} onClick={() => act(p.id, 'request-changes')}
                    data-testid={`poll-changes-${p.id}`}>
                    Send back with a note
                  </Button>
                  <Button size="sm" variant="outline"
                    className="border-rose-700 text-rose-100 hover:bg-rose-900 hover:text-white"
                    disabled={busy === p.id} onClick={() => act(p.id, 'decline')}
                    data-testid={`poll-decline-${p.id}`}>
                    Decline
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
